/**
 * Simple modal displaying the translated Terms & Conditions.
 */

import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { useConfigurations } from "../../../context/ConfigurationsContext/ConfigurationsContext";

type TermsLocale = "en" | "es" | "pt" | "fr" | "de" | "it" | "zh";

type TermsTranslation = {
  title: string;
  close: string;
  content: string;
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  zIndex: 2000,
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const normalizeLanguageName = (languageName = "") => {
  try {
    return languageName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  } catch {
    return languageName.toLowerCase();
  }
};

const resolveTermsLocale = (languageName?: string, flag?: string): TermsLocale => {
  const name = normalizeLanguageName(languageName);
  const flagUp = (flag ?? "").toUpperCase();

  if (
    name.includes("espanol") ||
    name.includes("spanish") ||
    ["MX", "ES", "AR", "CO", "CL", "PE"].includes(flagUp)
  ) {
    return "es";
  }

  if (
    name.includes("portugues") ||
    name.includes("portuguese") ||
    name === "pt" ||
    name.startsWith("pt-") ||
    ["BR", "PT"].includes(flagUp)
  ) {
    return "pt";
  }

  if (
    name.includes("francais") ||
    name.includes("french") ||
    name === "fr" ||
    name.startsWith("fr-") ||
    flagUp === "FR"
  ) {
    return "fr";
  }

  if (
    name.includes("deutsch") ||
    name.includes("german") ||
    name === "de" ||
    name.startsWith("de-") ||
    ["DE", "AT", "CH"].includes(flagUp)
  ) {
    return "de";
  }

  if (
    name.includes("italiano") ||
    name.includes("italian") ||
    name === "it" ||
    name.startsWith("it-") ||
    flagUp === "IT"
  ) {
    return "it";
  }

  if (
    name.includes("chinese") ||
    name.includes("中文") ||
    name === "zh" ||
    name.startsWith("zh-") ||
    ["CN", "TW", "HK"].includes(flagUp)
  ) {
    return "zh";
  }

  return "en";
};

const TERMS_I18N: Record<TermsLocale, TermsTranslation> = {
  en: {
    title: "Terms & Conditions",
    close: "CLOSE",
    content: `
      <h1>Terms &amp; Conditions</h1>
      <p>Welcome to Visitax Online!</p>
      <p>This website is operated by Visitax Online accessing or using this website, you agree to be bound by these Terms &amp; Conditions.</p>

      <h2>1. Purpose of the Website</h2>
      <p>Visitax Online allows international visitors traveling to Quintana Roo to register, complete the payment process related to the VISITAX tourist contribution, and obtain their corresponding QR confirmation.</p>

      <h2>2. Use of the Website</h2>
      <p>This website may only be used for lawful purposes and for your personal, non-commercial use.</p>
      <p>By using this site, you agree to:</p>
      <ul>
        <li>Provide accurate and complete information.</li>
        <li>Use the website only to process legitimate VISITAX tourist contribution payments.</li>
        <li>Not attempt to interfere with the operation or security of the website.</li>
        <li>Not copy, reproduce, distribute, or misuse any content available on the site.</li>
      </ul>

      <h2>3. Payments</h2>
      <p>Payments are processed through secure third-party payment platforms.</p>
      <p>Visitax Online does not store your banking or card information. All payment data is processed securely by authorized-certified payment providers.</p>
      <p>Before completing your transaction, you will be shown the total amount to be charged and will be required to confirm your acceptance of that amount before proceeding.</p>

      <h2>4. QR Confirmation</h2>
      <p>Once your payment is successfully processed, you will receive a QR confirmation code linked to the traveler information provided during your transaction.</p>
      <p>It is your responsibility to ensure that all traveler information entered is accurate before completing your payment.</p>

      <h2>5. Refund Policy</h2>
      <p>Once a payment has been successfully processed and the corresponding QR confirmation code has been issued, the transaction is considered completed and cannot be canceled or refunded.</p>
      <p>Please carefully review all traveler information before submitting your payment, as refunds will not be issued for errors resulting from incorrect information entered by the user.</p>
      <p>If you experience a technical issue during the payment process, please contact our customer support team for assistance.</p>

      <h2>6. Intellectual Property</h2>
      <p>All content on this website, including text, graphics, logos, designs, images, and software, is protected by applicable intellectual property laws and may not be copied, reproduced, modified, or distributed without prior written authorization.</p>

      <h2>7. Prohibited Activities</h2>
      <p>Users may not:</p>
      <ul>
        <li>Use the website for fraudulent purposes.</li>
        <li>Upload malicious software or viruses.</li>
        <li>Attempt unauthorized access to the website or its systems.</li>
        <li>Use automated tools, robots, or scraping technologies without authorization.</li>
        <li>Violate any applicable law or regulation.</li>
      </ul>

      <h2>8. Limitation of Liability</h2>
      <p>While we make every effort to ensure the information on this website is accurate and up to date, the website and its contents are offered on an "as is" basis.</p>
      <p>Visitax Online is not responsible for service interruptions, technical issues, third-party service failures, or damages arising from the use of the website.</p>

      <h2>9. Privacy</h2>
      <p>Your personal information is processed in accordance with our Privacy Notice.</p>
      <p>We are committed to protecting your personal data and using it solely for the purposes described therein.</p>

      <h2>10. Changes to These Terms</h2>
      <p>We may update these Terms &amp; Conditions from time to time. Any changes will become effective when published on this website.</p>

      <h2>11. Governing Law</h2>
      <p>These Terms &amp; Conditions are governed by and interpreted in accordance with the laws of Mexico. Any dispute related to the use of this website shall be submitted to the exclusive jurisdiction of the competent courts of Mexico City.</p>

      <h2>Contact</h2>
      <p>If you have questions regarding these Terms &amp; Conditions, please contact our customer support team through the contact information available on this website.</p>
    `,
  },
  es: {
    title: "Términos y condiciones",
    close: "CERRAR",
    content: `
      <h1>Términos y condiciones</h1>
      <p>¡Bienvenido a Visitax Online!</p>
      <p>Este sitio web es operado por Visitax Online Al acceder o utilizar este sitio web, aceptas quedar sujeto a estos Términos y condiciones.</p>

      <h2>1. Finalidad del sitio web</h2>
      <p>Visitax Online permite a los visitantes internacionales que viajan a Quintana Roo registrarse, completar el proceso de pago relacionado con la contribución turística VISITAX y obtener su confirmación QR correspondiente.</p>

      <h2>2. Uso del sitio web</h2>
      <p>Este sitio web solo puede utilizarse para fines lícitos y para uso personal, no comercial.</p>
      <p>Al usar este sitio, aceptas:</p>
      <ul>
        <li>Proporcionar información precisa y completa.</li>
        <li>Usar el sitio web únicamente para procesar pagos legítimos de la contribución turística VISITAX.</li>
        <li>No intentar interferir con la operación o seguridad del sitio web.</li>
        <li>No copiar, reproducir, distribuir ni hacer uso indebido de ningún contenido disponible en el sitio.</li>
      </ul>

      <h2>3. Pagos</h2>
      <p>Los pagos se procesan mediante plataformas de pago seguras de terceros.</p>
      <p>Visitax Online no almacena tu información bancaria ni de tarjeta. Todos los datos de pago son procesados de forma segura por proveedores de pago autorizados y certificados.</p>
      <p>Antes de completar tu transacción, se te mostrará el monto total a cobrar y deberás confirmar tu aceptación de dicho monto antes de continuar.</p>

      <h2>4. Confirmación QR</h2>
      <p>Una vez que tu pago se procese correctamente, recibirás un código de confirmación QR vinculado a la información del viajero proporcionada durante tu transacción.</p>
      <p>Es tu responsabilidad asegurarte de que toda la información del viajero ingresada sea correcta antes de completar tu pago.</p>

      <h2>5. Política de reembolsos</h2>
      <p>Una vez que el pago se haya procesado correctamente y se haya emitido el código de confirmación QR correspondiente, la transacción se considera completada y no podrá cancelarse ni reembolsarse.</p>
      <p>Revisa cuidadosamente toda la información del viajero antes de enviar tu pago, ya que no se emitirán reembolsos por errores derivados de información incorrecta ingresada por el usuario.</p>
      <p>Si experimentas un problema técnico durante el proceso de pago, comunícate con nuestro equipo de atención al cliente para recibir asistencia.</p>

      <h2>6. Propiedad intelectual</h2>
      <p>Todo el contenido de este sitio web, incluidos textos, gráficos, logotipos, diseños, imágenes y software, está protegido por las leyes de propiedad intelectual aplicables y no puede copiarse, reproducirse, modificarse ni distribuirse sin autorización previa por escrito.</p>

      <h2>7. Actividades prohibidas</h2>
      <p>Los usuarios no podrán:</p>
      <ul>
        <li>Usar el sitio web con fines fraudulentos.</li>
        <li>Cargar software malicioso o virus.</li>
        <li>Intentar acceder sin autorización al sitio web o a sus sistemas.</li>
        <li>Usar herramientas automatizadas, robots o tecnologías de extracción de datos sin autorización.</li>
        <li>Violar cualquier ley o regulación aplicable.</li>
      </ul>

      <h2>8. Limitación de responsabilidad</h2>
      <p>Aunque hacemos todo lo posible para asegurar que la información de este sitio web sea precisa y esté actualizada, el sitio web y su contenido se ofrecen "tal cual".</p>
      <p>Visitax Online no es responsable por interrupciones del servicio, problemas técnicos, fallas de servicios de terceros o daños derivados del uso del sitio web.</p>

      <h2>9. Privacidad</h2>
      <p>Tu información personal se procesa de acuerdo con nuestro Aviso de privacidad.</p>
      <p>Nos comprometemos a proteger tus datos personales y a utilizarlos únicamente para los fines descritos en dicho aviso.</p>

      <h2>10. Cambios a estos términos</h2>
      <p>Podemos actualizar estos Términos y condiciones ocasionalmente. Cualquier cambio entrará en vigor cuando sea publicado en este sitio web.</p>

      <h2>11. Ley aplicable</h2>
      <p>Estos Términos y condiciones se rigen e interpretan de acuerdo con las leyes de México. Cualquier disputa relacionada con el uso de este sitio web se someterá a la jurisdicción exclusiva de los tribunales competentes de la Ciudad de México.</p>

      <h2>Contacto</h2>
      <p>Si tienes preguntas sobre estos Términos y condiciones, comunícate con nuestro equipo de atención al cliente a través de la información de contacto disponible en este sitio web.</p>
    `,
  },
  pt: {
    title: "Termos e condições",
    close: "FECHAR",
    content: `
      <h1>Termos e condições</h1>
      <p>Bem-vindo ao Visitax Online!</p>
      <p>Este site é operado por Visitax Online Ao acessar ou usar este site, você concorda em cumprir estes Termos e condições.</p>
      <h2>1. Finalidade do site</h2>
      <p>O Visitax Online permite que visitantes internacionais que viajam para Quintana Roo se registrem, concluam o processo de pagamento relacionado à contribuição turística VISITAX e obtenham a confirmação QR correspondente.</p>
      <h2>2. Uso do site</h2>
      <p>Este site só pode ser usado para fins lícitos e para uso pessoal, não comercial.</p>
      <p>Ao usar este site, você concorda em fornecer informações precisas e completas, usar o site somente para pagamentos legítimos da contribuição turística VISITAX, não interferir na operação ou segurança do site e não copiar, reproduzir, distribuir ou usar indevidamente qualquer conteúdo disponível.</p>
      <h2>3. Pagamentos</h2>
      <p>Os pagamentos são processados por plataformas seguras de pagamento de terceiros. O Visitax Online não armazena suas informações bancárias ou de cartão. Todos os dados de pagamento são processados com segurança por provedores de pagamento autorizados e certificados.</p>
      <p>Antes de concluir a transação, o valor total a ser cobrado será exibido e você deverá confirmar sua aceitação antes de prosseguir.</p>
      <h2>4. Confirmação QR</h2>
      <p>Após o processamento bem-sucedido do pagamento, você receberá um código de confirmação QR vinculado às informações do viajante fornecidas durante a transação. É sua responsabilidade garantir que todas as informações estejam corretas antes de concluir o pagamento.</p>
      <h2>5. Política de reembolso</h2>
      <p>Após o processamento bem-sucedido do pagamento e a emissão do código QR correspondente, a transação é considerada concluída e não poderá ser cancelada nem reembolsada.</p>
      <p>Revise cuidadosamente todas as informações do viajante antes de enviar o pagamento, pois não serão emitidos reembolsos por erros decorrentes de informações incorretas inseridas pelo usuário. Em caso de problema técnico durante o pagamento, entre em contato com nossa equipe de suporte ao cliente.</p>
      <h2>6. Propriedade intelectual</h2>
      <p>Todo o conteúdo deste site, incluindo textos, gráficos, logotipos, designs, imagens e software, é protegido pelas leis de propriedade intelectual aplicáveis e não pode ser copiado, reproduzido, modificado ou distribuído sem autorização prévia por escrito.</p>
      <h2>7. Atividades proibidas</h2>
      <p>Os usuários não podem usar o site para fins fraudulentos, carregar software malicioso ou vírus, tentar acesso não autorizado, usar ferramentas automatizadas, robôs ou tecnologias de extração sem autorização, nem violar qualquer lei ou regulamento aplicável.</p>
      <h2>8. Limitação de responsabilidade</h2>
      <p>Embora façamos todos os esforços para garantir que as informações deste site sejam precisas e atualizadas, o site e seu conteúdo são oferecidos "como estão". Visitax Online não é responsável por interrupções de serviço, problemas técnicos, falhas de serviços de terceiros ou danos decorrentes do uso do site.</p>
      <h2>9. Privacidade</h2>
      <p>Suas informações pessoais são processadas de acordo com nosso Aviso de Privacidade. Estamos comprometidos em proteger seus dados pessoais e usá-los somente para os fins nele descritos.</p>
      <h2>10. Alterações destes termos</h2>
      <p>Podemos atualizar estes Termos e condições periodicamente. Qualquer alteração entrará em vigor quando publicada neste site.</p>
      <h2>11. Lei aplicável</h2>
      <p>Estes Termos e condições são regidos e interpretados de acordo com as leis do México. Qualquer disputa relacionada ao uso deste site será submetida à jurisdição exclusiva dos tribunais competentes da Cidade do México.</p>
      <h2>Contato</h2>
      <p>Se tiver dúvidas sobre estes Termos e condições, entre em contato com nossa equipe de suporte ao cliente por meio das informações de contato disponíveis neste site.</p>
    `,
  },
  fr: {
    title: "Conditions générales",
    close: "FERMER",
    content: `
      <h1>Conditions générales</h1>
      <p>Bienvenue sur Visitax Online !</p>
      <p>Ce site web est exploité par Visitax Online En accédant à ce site ou en l'utilisant, vous acceptez d'être lié par les présentes Conditions générales.</p>
      <h2>1. Objet du site web</h2>
      <p>Visitax Online permet aux visiteurs internationaux se rendant à Quintana Roo de s'enregistrer, d'effectuer le processus de paiement lié à la contribution touristique VISITAX et d'obtenir leur confirmation QR correspondante.</p>
      <h2>2. Utilisation du site web</h2>
      <p>Ce site web ne peut être utilisé qu'à des fins légales et pour un usage personnel et non commercial.</p>
      <p>En utilisant ce site, vous acceptez de fournir des informations exactes et complètes, d'utiliser le site uniquement pour traiter des paiements légitimes de la contribution touristique VISITAX, de ne pas interférer avec son fonctionnement ou sa sécurité et de ne pas copier, reproduire, distribuer ni utiliser abusivement son contenu.</p>
      <h2>3. Paiements</h2>
      <p>Les paiements sont traités par des plateformes de paiement tierces sécurisées. Visitax Online ne stocke pas vos informations bancaires ou de carte. Toutes les données de paiement sont traitées de manière sécurisée par des prestataires de paiement autorisés et certifiés.</p>
      <p>Avant de finaliser votre transaction, le montant total à débiter vous sera présenté et vous devrez confirmer votre acceptation avant de continuer.</p>
      <h2>4. Confirmation QR</h2>
      <p>Une fois votre paiement traité avec succès, vous recevrez un code de confirmation QR lié aux informations du voyageur fournies pendant votre transaction. Il vous appartient de vérifier que toutes les informations sont exactes avant de finaliser votre paiement.</p>
      <h2>5. Politique de remboursement</h2>
      <p>Une fois le paiement traité avec succès et le code de confirmation QR correspondant émis, la transaction est considérée comme terminée et ne peut être ni annulée ni remboursée.</p>
      <p>Veuillez vérifier attentivement toutes les informations du voyageur avant de soumettre votre paiement, car aucun remboursement ne sera accordé pour les erreurs résultant d'informations incorrectes saisies par l'utilisateur. En cas de problème technique pendant le paiement, contactez notre service client.</p>
      <h2>6. Propriété intellectuelle</h2>
      <p>Tout le contenu de ce site web, y compris les textes, graphiques, logos, designs, images et logiciels, est protégé par les lois applicables en matière de propriété intellectuelle et ne peut être copié, reproduit, modifié ou distribué sans autorisation écrite préalable.</p>
      <h2>7. Activités interdites</h2>
      <p>Les utilisateurs ne peuvent pas utiliser le site à des fins frauduleuses, téléverser des logiciels malveillants ou des virus, tenter un accès non autorisé, utiliser des outils automatisés, robots ou technologies d'extraction sans autorisation, ni violer toute loi ou réglementation applicable.</p>
      <h2>8. Limitation de responsabilité</h2>
      <p>Bien que nous fassions tout notre possible pour garantir que les informations du site soient exactes et à jour, le site et son contenu sont fournis "en l'état". Visitax Online n'est pas responsable des interruptions de service, problèmes techniques, défaillances de services tiers ou dommages résultant de l'utilisation du site.</p>
      <h2>9. Confidentialité</h2>
      <p>Vos informations personnelles sont traitées conformément à notre Avis de confidentialité. Nous nous engageons à protéger vos données personnelles et à les utiliser uniquement aux fins qui y sont décrites.</p>
      <h2>10. Modifications des présentes conditions</h2>
      <p>Nous pouvons mettre à jour ces Conditions générales de temps à autre. Toute modification prendra effet dès sa publication sur ce site web.</p>
      <h2>11. Droit applicable</h2>
      <p>Ces Conditions générales sont régies et interprétées conformément aux lois du Mexique. Tout litige lié à l'utilisation de ce site sera soumis à la juridiction exclusive des tribunaux compétents de Mexico.</p>
      <h2>Contact</h2>
      <p>Pour toute question concernant ces Conditions générales, veuillez contacter notre équipe de support client via les informations de contact disponibles sur ce site web.</p>
    `,
  },
  de: {
    title: "Allgemeine Geschäftsbedingungen",
    close: "SCHLIESSEN",
    content: `
      <h1>Allgemeine Geschäftsbedingungen</h1>
      <p>Willkommen bei Visitax Online!</p>
      <p>Diese Website wird von Visitax Online betrieben. Durch den Zugriff auf diese Website oder deren Nutzung erklären Sie sich mit diesen Allgemeinen Geschäftsbedingungen einverstanden.</p>
      <h2>1. Zweck der Website</h2>
      <p>Visitax Online ermöglicht internationalen Besuchern, die nach Quintana Roo reisen, sich zu registrieren, den Zahlungsvorgang für den VISITAX-Tourismusbeitrag abzuschließen und die entsprechende QR-Bestätigung zu erhalten.</p>
      <h2>2. Nutzung der Website</h2>
      <p>Diese Website darf nur für rechtmäßige Zwecke und für Ihren persönlichen, nicht kommerziellen Gebrauch genutzt werden.</p>
      <p>Mit der Nutzung dieser Website erklären Sie sich damit einverstanden, genaue und vollständige Informationen bereitzustellen, die Website nur für legitime Zahlungen des VISITAX-Tourismusbeitrags zu verwenden, den Betrieb oder die Sicherheit der Website nicht zu beeinträchtigen und keine Inhalte zu kopieren, zu vervielfältigen, zu verbreiten oder missbräuchlich zu verwenden.</p>
      <h2>3. Zahlungen</h2>
      <p>Zahlungen werden über sichere Zahlungsplattformen Dritter abgewickelt. Visitax Online speichert keine Bank- oder Kartendaten. Alle Zahlungsdaten werden sicher von autorisierten und zertifizierten Zahlungsanbietern verarbeitet.</p>
      <p>Vor Abschluss Ihrer Transaktion wird Ihnen der zu belastende Gesamtbetrag angezeigt, und Sie müssen Ihre Zustimmung zu diesem Betrag bestätigen, bevor Sie fortfahren.</p>
      <h2>4. QR-Bestätigung</h2>
      <p>Nach erfolgreicher Zahlungsabwicklung erhalten Sie einen QR-Bestätigungscode, der mit den während der Transaktion angegebenen Reisendeninformationen verknüpft ist. Sie sind dafür verantwortlich, sicherzustellen, dass alle Angaben korrekt sind, bevor Sie die Zahlung abschließen.</p>
      <h2>5. Rückerstattungsrichtlinie</h2>
      <p>Sobald eine Zahlung erfolgreich verarbeitet und der entsprechende QR-Bestätigungscode ausgestellt wurde, gilt die Transaktion als abgeschlossen und kann nicht storniert oder erstattet werden.</p>
      <p>Bitte prüfen Sie alle Reisendeninformationen sorgfältig, bevor Sie Ihre Zahlung absenden, da für Fehler aufgrund falsch eingegebener Informationen keine Rückerstattungen erfolgen. Bei technischen Problemen während des Zahlungsvorgangs wenden Sie sich bitte an unser Kundensupport-Team.</p>
      <h2>6. Geistiges Eigentum</h2>
      <p>Alle Inhalte dieser Website, einschließlich Texte, Grafiken, Logos, Designs, Bilder und Software, sind durch geltende Gesetze zum geistigen Eigentum geschützt und dürfen ohne vorherige schriftliche Genehmigung nicht kopiert, vervielfältigt, geändert oder verbreitet werden.</p>
      <h2>7. Verbotene Aktivitäten</h2>
      <p>Nutzer dürfen die Website nicht für betrügerische Zwecke verwenden, keine Schadsoftware oder Viren hochladen, keinen unbefugten Zugriff versuchen, keine automatisierten Tools, Roboter oder Scraping-Technologien ohne Genehmigung einsetzen und keine geltenden Gesetze oder Vorschriften verletzen.</p>
      <h2>8. Haftungsbeschränkung</h2>
      <p>Obwohl wir uns bemühen, sicherzustellen, dass die Informationen auf dieser Website korrekt und aktuell sind, werden die Website und ihre Inhalte "wie besehen" bereitgestellt. Visitax Online haftet nicht für Dienstunterbrechungen, technische Probleme, Ausfälle von Drittanbieterdiensten oder Schäden, die aus der Nutzung der Website entstehen.</p>
      <h2>9. Datenschutz</h2>
      <p>Ihre personenbezogenen Daten werden gemäß unserem Datenschutzhinweis verarbeitet. Wir verpflichten uns, Ihre personenbezogenen Daten zu schützen und sie nur für die dort beschriebenen Zwecke zu verwenden.</p>
      <h2>10. Änderungen dieser Bedingungen</h2>
      <p>Wir können diese Allgemeinen Geschäftsbedingungen von Zeit zu Zeit aktualisieren. Änderungen werden wirksam, sobald sie auf dieser Website veröffentlicht werden.</p>
      <h2>11. Anwendbares Recht</h2>
      <p>Diese Allgemeinen Geschäftsbedingungen unterliegen den Gesetzen Mexikos und werden nach diesen ausgelegt. Streitigkeiten im Zusammenhang mit der Nutzung dieser Website unterliegen der ausschließlichen Zuständigkeit der zuständigen Gerichte von Mexiko-Stadt.</p>
      <h2>Kontakt</h2>
      <p>Wenn Sie Fragen zu diesen Allgemeinen Geschäftsbedingungen haben, kontaktieren Sie bitte unser Kundensupport-Team über die auf dieser Website verfügbaren Kontaktinformationen.</p>
    `,
  },
  it: {
    title: "Termini e condizioni",
    close: "CHIUDI",
    content: `
      <h1>Termini e condizioni</h1>
      <p>Benvenuto su Visitax Online!</p>
      <p>Questo sito web è gestito da Visitax Online Accedendo o utilizzando questo sito web, accetti di essere vincolato dai presenti Termini e condizioni.</p>
      <h2>1. Scopo del sito web</h2>
      <p>Visitax Online consente ai visitatori internazionali che viaggiano a Quintana Roo di registrarsi, completare il processo di pagamento relativo al contributo turistico VISITAX e ottenere la relativa conferma QR.</p>
      <h2>2. Uso del sito web</h2>
      <p>Questo sito web può essere utilizzato solo per scopi leciti e per uso personale, non commerciale.</p>
      <p>Utilizzando questo sito, accetti di fornire informazioni accurate e complete, usare il sito solo per pagamenti legittimi del contributo turistico VISITAX, non interferire con il funzionamento o la sicurezza del sito e non copiare, riprodurre, distribuire o utilizzare impropriamente i contenuti disponibili.</p>
      <h2>3. Pagamenti</h2>
      <p>I pagamenti sono elaborati tramite piattaforme di pagamento sicure di terze parti. Visitax Online non memorizza le tue informazioni bancarie o della carta. Tutti i dati di pagamento sono elaborati in modo sicuro da fornitori di pagamento autorizzati e certificati.</p>
      <p>Prima di completare la transazione, ti verrà mostrato l'importo totale da addebitare e dovrai confermare la tua accettazione prima di procedere.</p>
      <h2>4. Conferma QR</h2>
      <p>Una volta elaborato correttamente il pagamento, riceverai un codice di conferma QR collegato alle informazioni del viaggiatore fornite durante la transazione. È tua responsabilità assicurarti che tutte le informazioni siano corrette prima di completare il pagamento.</p>
      <h2>5. Politica di rimborso</h2>
      <p>Una volta che il pagamento è stato elaborato correttamente e il relativo codice di conferma QR è stato emesso, la transazione è considerata completata e non può essere annullata o rimborsata.</p>
      <p>Controlla attentamente tutte le informazioni del viaggiatore prima di inviare il pagamento, poiché non saranno emessi rimborsi per errori derivanti da informazioni errate inserite dall'utente. In caso di problema tecnico durante il pagamento, contatta il nostro team di assistenza clienti.</p>
      <h2>6. Proprietà intellettuale</h2>
      <p>Tutti i contenuti di questo sito web, inclusi testi, grafica, loghi, design, immagini e software, sono protetti dalle leggi applicabili sulla proprietà intellettuale e non possono essere copiati, riprodotti, modificati o distribuiti senza previa autorizzazione scritta.</p>
      <h2>7. Attività vietate</h2>
      <p>Gli utenti non possono usare il sito per scopi fraudolenti, caricare software dannosi o virus, tentare accessi non autorizzati, usare strumenti automatizzati, robot o tecnologie di scraping senza autorizzazione, né violare leggi o regolamenti applicabili.</p>
      <h2>8. Limitazione di responsabilità</h2>
      <p>Sebbene facciamo ogni sforzo per garantire che le informazioni su questo sito siano accurate e aggiornate, il sito e i suoi contenuti sono forniti "così come sono". Visitax Online non è responsabile per interruzioni del servizio, problemi tecnici, guasti di servizi di terze parti o danni derivanti dall'uso del sito.</p>
      <h2>9. Privacy</h2>
      <p>Le tue informazioni personali sono trattate in conformità con la nostra Informativa sulla privacy. Ci impegniamo a proteggere i tuoi dati personali e a utilizzarli solo per le finalità ivi descritte.</p>
      <h2>10. Modifiche ai presenti termini</h2>
      <p>Possiamo aggiornare periodicamente questi Termini e condizioni. Qualsiasi modifica avrà effetto quando sarà pubblicata su questo sito web.</p>
      <h2>11. Legge applicabile</h2>
      <p>I presenti Termini e condizioni sono regolati e interpretati secondo le leggi del Messico. Qualsiasi controversia relativa all'uso di questo sito web sarà sottoposta alla giurisdizione esclusiva dei tribunali competenti di Città del Messico.</p>
      <h2>Contatto</h2>
      <p>In caso di domande sui presenti Termini e condizioni, contatta il nostro team di assistenza clienti tramite le informazioni di contatto disponibili su questo sito web.</p>
    `,
  },
  zh: {
    title: "条款和条件",
    close: "关闭",
    content: `
      <h1>条款和条件</h1>
      <p>欢迎使用 Visitax Online！</p>
      <p>本网站由 Visitax Online 运营。访问或使用本网站即表示您同意受本条款和条件的约束。</p>
      <h2>1. 网站目的</h2>
      <p>Visitax Online 允许前往金塔纳罗奥州的国际游客注册、完成与 VISITAX 旅游贡献相关的付款流程，并获得相应的二维码确认。</p>
      <h2>2. 网站使用</h2>
      <p>本网站仅可用于合法目的以及个人、非商业用途。</p>
      <p>使用本网站即表示您同意提供准确完整的信息，仅使用本网站处理合法的 VISITAX 旅游贡献付款，不干扰网站运行或安全，也不复制、转载、分发或滥用网站上的任何内容。</p>
      <h2>3. 付款</h2>
      <p>付款通过安全的第三方支付平台处理。Visitax Online 不存储您的银行或银行卡信息。所有付款数据均由经授权和认证的支付服务提供商安全处理。</p>
      <p>在完成交易前，系统会显示将收取的总金额，您必须确认接受该金额后方可继续。</p>
      <h2>4. 二维码确认</h2>
      <p>付款成功处理后，您将收到与交易期间提供的旅客信息相关联的二维码确认码。您有责任在完成付款前确保所有旅客信息准确无误。</p>
      <h2>5. 退款政策</h2>
      <p>付款成功处理并签发相应二维码确认码后，交易即视为完成，不能取消或退款。</p>
      <p>提交付款前请仔细核对所有旅客信息。因用户输入错误信息造成的问题将不予退款。如在付款过程中遇到技术问题，请联系我们的客户支持团队寻求帮助。</p>
      <h2>6. 知识产权</h2>
      <p>本网站的所有内容，包括文字、图形、标志、设计、图片和软件，均受适用知识产权法律保护，未经事先书面授权不得复制、转载、修改或分发。</p>
      <h2>7. 禁止活动</h2>
      <p>用户不得将本网站用于欺诈目的，不得上传恶意软件或病毒，不得尝试未经授权访问网站或其系统，不得未经授权使用自动化工具、机器人或抓取技术，也不得违反任何适用法律或法规。</p>
      <h2>8. 责任限制</h2>
      <p>尽管我们尽力确保本网站信息准确且及时更新，但本网站及其内容均按“现状”提供。Visitax Online 对服务中断、技术问题、第三方服务故障或因使用本网站而产生的损害不承担责任。</p>
      <h2>9. 隐私</h2>
      <p>您的个人信息将根据我们的隐私声明处理。我们承诺保护您的个人数据，并仅将其用于该声明中描述的目的。</p>
      <h2>10. 条款变更</h2>
      <p>我们可能会不时更新本条款和条件。任何变更将在本网站发布时生效。</p>
      <h2>11. 适用法律</h2>
      <p>本条款和条件受墨西哥法律管辖并按其解释。与使用本网站有关的任何争议均应提交墨西哥城有管辖权的法院专属管辖。</p>
      <h2>联系方式</h2>
      <p>如果您对本条款和条件有任何疑问，请通过本网站提供的联系信息联系我们的客户支持团队。</p>
    `,
  },
};

export default function ModalText({
  open = false,
  setOpen = (state: boolean) => {
    state;
  },
}) {
  const configurations = useConfigurations();
  const currentLanguageName =
    configurations.lenguageList?.[configurations.currentlenguage];
  const currentFlag = configurations.flags?.[configurations.currentlenguage];
  const locale = resolveTermsLocale(currentLanguageName, currentFlag);
  const translation = TERMS_I18N[locale] ?? TERMS_I18N.en;

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} className="customized-dialog-title">
        {translation.title}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Typography
          component="div"
          gutterBottom
          style={{ textAlign: "justify" }}
          dangerouslySetInnerHTML={{ __html: translation.content }}
        />
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleClose}>
          {translation.close}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
